"use client";

import { useEffect, useRef } from "react";
import { usePathname } from "next/navigation";

// 자체 히트맵 클라이언트 트래커.
// 원시 좌표를 보내지 않고, 그리드 셀로 집계한 밀도만 페이지뷰당 1~N번 비콘 전송.
// - 클릭: 모든 기기
// - 마우스 이동: 데스크톱(pointer:fine)만, throttle 후 집계
// - 스크롤: 페이지뷰당 최대 도달 깊이(%) 1회
const CELL = 16; // x/y 버킷 픽셀
const X_MAX = 400; // x 버킷 한계 (절대 px / CELL)
const Y_MAX = 8000;
const WBIN = 80; // 폭 버킷 단위(px): 방문자 뷰포트 폭을 80px 단위로 묶음
const MOVE_THROTTLE_MS = 120;
const FLUSH_INTERVAL_MS = 15000;
const MAX_CELLS = 1500;
const TRACK_URL = "/api/heatmap/track";
// 로컬 admin 인스턴스(ADMIN_MODE)는 루트 경로를 /admin/*로 rewrite하므로
// 브라우저 경로가 /admin이 아니어도 admin 화면이다. 이 빌드에서는 추적 자체를 끈다.
const ADMIN_MODE = process.env.NEXT_PUBLIC_ADMIN_MODE === "1";

function detectDevice(): "desktop" | "mobile" {
  const coarse = window.matchMedia?.("(pointer: coarse)")?.matches ?? false;
  const narrow = window.innerWidth < 768;
  return coarse || narrow ? "mobile" : "desktop";
}

// X/Y 모두 절대 좌표(px) 버킷. 반응형 정합은 width_bucket(폭 구간)별 분리 +
// Admin이 해당 폭으로 미리보기를 렌더하는 방식으로 해결한다.
function bucketKey(clientX: number, pageY: number): string {
  const x = Math.min(Math.max(Math.floor(clientX / CELL), 0), X_MAX);
  const y = Math.min(Math.max(Math.floor(pageY / CELL), 0), Y_MAX);
  return `${x}:${y}`;
}

// 방문자 뷰포트 폭을 WBIN 단위로 묶은 폭 버킷.
function widthBucket(): number {
  const vw = window.innerWidth || document.documentElement.clientWidth || 1;
  return Math.round(vw / WBIN) * WBIN;
}

// "x:y" 카운트 맵 -> [[x,y,w], ...] (가중치 상위 MAX_CELLS만)
function serialize(map: Map<string, number>): [number, number, number][] {
  const out: [number, number, number][] = [];
  for (const [k, w] of map) {
    const [x, y] = k.split(":");
    out.push([Number(x), Number(y), w]);
  }
  if (out.length > MAX_CELLS) {
    out.sort((a, b) => b[2] - a[2]);
    out.length = MAX_CELLS;
  }
  return out;
}

export function HeatmapTracker() {
  const pathname = usePathname();
  const pathRef = useRef(pathname);

  const clickCells = useRef(new Map<string, number>());
  const moveCells = useRef(new Map<string, number>());
  const scrollMax = useRef(0); // 0..1
  const sentFirst = useRef(false);
  const scrollSent = useRef(false);
  const lastMove = useRef(0);
  const deviceRef = useRef<"desktop" | "mobile">("desktop");

  // SPA 네비게이션: 경로 변경 시 이전 경로 데이터를 최종 flush 후 리셋
  useEffect(() => {
    if (pathRef.current !== pathname) {
      flush(true);
      clickCells.current.clear();
      moveCells.current.clear();
      scrollMax.current = 0;
      sentFirst.current = false;
      scrollSent.current = false;
      pathRef.current = pathname;
      // 새 경로 페이지뷰를 즉시 1회 전송 (15초/이탈 대기 없이).
      flush(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  function flush(isFinal: boolean) {
    if (ADMIN_MODE) return;
    // iframe 안(히트맵 미리보기 배경 등)에서는 추적하지 않음
    if (typeof window !== "undefined" && window.top !== window.self) return;
    const path = pathRef.current;
    if (!path || path.startsWith("/admin") || path.startsWith("/heatmap-view")) return;
    if (typeof navigator === "undefined" || !navigator.sendBeacon) return;

    const ck = serialize(clickCells.current);
    const mv = serialize(moveCells.current);
    const sendScroll = isFinal && !scrollSent.current;
    const first = !sentFirst.current;

    if (ck.length === 0 && mv.length === 0 && !sendScroll && !first) return;

    const payload = {
      p: path,
      d: deviceRef.current,
      w: widthBucket(),
      ck,
      mv,
      s: sendScroll ? Math.round(scrollMax.current * 100) : null,
      f: first,
    };

    try {
      const blob = new Blob([JSON.stringify(payload)], {
        type: "application/json",
      });
      navigator.sendBeacon(TRACK_URL, blob);
      sentFirst.current = true;
      if (sendScroll) scrollSent.current = true;
      clickCells.current.clear();
      moveCells.current.clear();
    } catch {
      // 무시 (추적 실패가 사용자 경험에 영향 주지 않도록)
    }
  }

  useEffect(() => {
    if (ADMIN_MODE) return;
    // iframe 안(미리보기)에서는 리스너를 붙이지 않음
    if (window.top !== window.self) return;
    deviceRef.current = detectDevice();
    const isDesktop = deviceRef.current === "desktop";

    const onClick = (e: MouseEvent) => {
      const k = bucketKey(e.clientX, e.pageY);
      clickCells.current.set(k, (clickCells.current.get(k) ?? 0) + 1);
    };

    const onMove = (e: MouseEvent) => {
      const now = Date.now();
      if (now - lastMove.current < MOVE_THROTTLE_MS) return;
      lastMove.current = now;
      const k = bucketKey(e.clientX, e.pageY);
      moveCells.current.set(k, (moveCells.current.get(k) ?? 0) + 1);
    };

    const onScroll = () => {
      const dh = Math.max(document.documentElement.scrollHeight, 1);
      const depth = (window.scrollY + window.innerHeight) / dh;
      scrollMax.current = Math.min(Math.max(depth, scrollMax.current), 1);
    };

    const onHidden = () => {
      if (document.visibilityState === "hidden") flush(true);
    };

    document.addEventListener("click", onClick, { capture: true, passive: true });
    if (isDesktop) {
      document.addEventListener("mousemove", onMove, { passive: true });
    }
    window.addEventListener("scroll", onScroll, { passive: true });
    document.addEventListener("visibilitychange", onHidden);
    window.addEventListener("pagehide", () => flush(true));

    // 초기 1회: 페이지뷰 카운트 보장. 첫 비콘을 마운트 직후 즉시 전송하여
    // 15초 미만 체류(바운스)나 sendBeacon 이탈 손실로 페이지뷰가 누락되지 않게 한다.
    onScroll();
    flush(false);
    const interval = window.setInterval(() => flush(false), FLUSH_INTERVAL_MS);

    return () => {
      flush(true);
      document.removeEventListener("click", onClick, { capture: true });
      if (isDesktop) document.removeEventListener("mousemove", onMove);
      window.removeEventListener("scroll", onScroll);
      document.removeEventListener("visibilitychange", onHidden);
      window.clearInterval(interval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return null;
}
